import { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../hooks/useAuth";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { 
  Newspaper, Calendar, Users, BookOpen, Video, Film, 
  MapPin, Plus, Edit, Trash2, Loader2, Save, X,
  LayoutDashboard, ChevronRight, UserCog, Shield, CheckCircle, XCircle, Clock, Key, Download, Upload, Heart, Activity, MessageSquare, Briefcase, UserPlus, GripVertical, List, Mail, Send, Eye, ShieldX, FileText, Pencil, Crown, Linkedin, Twitter, Instagram, Youtube, Globe, ExternalLink
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sections with role restrictions
// superAdminOnly: true means only super_admin can see this section
const allSections = [
  { name: "Activity", path: "activity", icon: Activity, superAdminOnly: true },
  { name: "News", path: "news", icon: Newspaper },
  { name: "Festivals", path: "festivals", icon: Calendar },
  { name: "Directors", path: "directors", icon: Users },
  { name: "Resource Lists", path: "resource-lists", icon: List },
  { name: "Workshops", path: "workshops", icon: Video },
  { name: "Films", path: "films", icon: Film },
  { name: "Events", path: "events", icon: MapPin },
  { name: "Submissions", path: "submissions", icon: Film },
  { name: "Jobs", path: "jobs", icon: CheckCircle },
  { name: "Volunteers", path: "volunteers", icon: Heart },
  { name: "Newsletter", path: "newsletter", icon: Mail },
  { name: "Founders", path: "founders", icon: Crown, superAdminOnly: true },
  { name: "Board & Advisors", path: "board", icon: Shield, superAdminOnly: true },
  { name: "Site Content", path: "content", icon: FileText, superAdminOnly: true },
  { name: "Users", path: "users", icon: UserCog, superAdminOnly: true },
  { name: "Backup", path: "backup", icon: Download, superAdminOnly: true },
];

export default function AdminDashboard() {
  const location = useLocation();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  
  // Filter sections based on role
  const sections = allSections.filter(section => 
    !section.superAdminOnly || isSuperAdmin
  );
  
  const currentSection = location.pathname.split("/admin/")[1] || (isSuperAdmin ? "activity" : "news");

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-6 h-6 text-purple-light" />
            <h1 className="font-syne text-2xl font-bold text-white">Admin Dashboard</h1>
          </div>
          {!isSuperAdmin && (
            <span className="text-xs bg-purple/20 text-purple-light px-3 py-1 rounded-full">
              Admin Access
            </span>
          )}
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1 sticky top-24">
              {sections.map((section) => (
                <Link
                  key={section.path}
                  to={`/admin/${section.path}`}
                  data-testid={`admin-nav-${section.path}`}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    currentSection === section.path
                      ? "bg-purple/20 text-purple-light border-l-2 border-purple"
                      : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <section.icon className="w-5 h-5" />
                  {section.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-4">
            <Routes>
              <Route path="activity" element={isSuperAdmin ? <AdminRecentActivity /> : <AccessDenied />} />
              <Route path="news" element={<AdminNews />} />
              <Route path="festivals" element={<AdminFestivals />} />
              <Route path="directors" element={<AdminDirectors />} />
              <Route path="resource-lists" element={<AdminResourceLists />} />
              <Route path="workshops" element={<AdminWorkshops />} />
              <Route path="films" element={<AdminFilms />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="submissions" element={<AdminSubmissions />} />
              <Route path="jobs" element={<AdminJobs />} />
              <Route path="volunteers" element={<AdminVolunteers />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
              <Route path="founders" element={isSuperAdmin ? <AdminFounders /> : <AccessDenied />} />
              <Route path="board" element={isSuperAdmin ? <AdminBoardMembers /> : <AccessDenied />} />
              <Route path="content" element={isSuperAdmin ? <AdminSiteContent /> : <AccessDenied />} />
              <Route path="users" element={isSuperAdmin ? <AdminUsers /> : <AccessDenied />} />
              <Route path="backup" element={isSuperAdmin ? <AdminBackup /> : <AccessDenied />} />
              <Route path="*" element={isSuperAdmin ? <AdminRecentActivity /> : <AdminNews />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}

// Access Denied Component for restricted routes
function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <ShieldX className="w-16 h-16 text-red-500 mb-4" />
      <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
      <p className="text-slate-400">
        You don't have permission to view this section.
      </p>
    </div>
  );
}

// Recent Activity Dashboard Component
function AdminRecentActivity() {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActivity();
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API}/admin/recent-activity`, { withCredentials: true });
      setActivity(response.data);
    } catch (error) {
      toast.error("Failed to fetch recent activity");
    } finally {
      setLoading(false);
    }
  };

  // Parse date string as local date (not UTC) to prevent timezone shift
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date();
    if (dateStr.length === 10 && dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = parseLocalDate(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400",
      approved: "bg-green-500/20 text-green-400",
      rejected: "bg-red-500/20 text-red-400",
      published: "bg-purple-500/20 text-purple-400",
    };
    return styles[status] || "bg-slate-500/20 text-slate-400";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple" />
      </div>
    );
  }

  const ActivityCard = ({ title, icon: Icon, items, emptyText, renderItem, onViewAll }) => (
    <div className="bg-void-surface border border-white/5 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-syne text-white flex items-center gap-2">
          <Icon className="w-4 h-4 text-purple-light" />
          {title}
        </h3>
        {onViewAll && items?.length > 0 && (
          <button 
            onClick={onViewAll}
            className="text-xs text-purple-light hover:text-purple transition-colors"
          >
            View all →
          </button>
        )}
      </div>
      {items?.length > 0 ? (
        <div className="space-y-3">
          {items.map((item, idx) => renderItem(item, idx))}
        </div>
      ) : (
        <p className="text-slate-500 text-sm">{emptyText}</p>
      )}
    </div>
  );

  return (
    <div>
      <h2 className="font-syne text-xl font-bold text-white mb-6 flex items-center gap-2">
        <Activity className="w-5 h-5 text-purple-light" />
        Recent Activity
      </h2>
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Film Submissions */}
        <ActivityCard
          title="Film Submissions"
          icon={Film}
          items={activity?.submissions}
          emptyText="No recent submissions"
          onViewAll={() => navigate('/admin/submissions')}
          renderItem={(item, idx) => (
            <div key={idx} className="flex items-start justify-between gap-3 pb-3 border-b border-white/5 last:border-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm truncate">{item.title}</p>
                <p className="text-slate-500 text-xs">by {item.director}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(item.status)}`}>
                  {item.status}
                </span>
                <span className="text-slate-500 text-xs">{formatDate(item.created_at)}</span>
              </div>
            </div>
          )}
        />

        {/* Forum Posts */}
        <ActivityCard
          title="Forum Posts"
          icon={MessageSquare}
          items={activity?.forum_posts}
          emptyText="No recent forum posts"
          renderItem={(item, idx) => (
            <div key={idx} className="pb-3 border-b border-white/5 last:border-0 last:pb-0">
              <p className="text-white text-sm truncate">{item.title}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-slate-500 text-xs">by {item.author_name}</span>
                <span className="text-slate-500 text-xs">{formatDate(item.created_at)}</span>
              </div>
            </div>
          )}
        />

        {/* Job Postings */}
        <ActivityCard
          title="Job Postings"
          icon={Briefcase}
          items={activity?.jobs}
          emptyText="No recent job postings"
          onViewAll={() => navigate('/admin/jobs')}
          renderItem={(item, idx) => (
            <div key={idx} className="flex items-start justify-between gap-3 pb-3 border-b border-white/5 last:border-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm truncate">{item.title}</p>
                <p className="text-slate-500 text-xs">{item.company}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(item.status)}`}>
                  {item.status}
                </span>
                <span className="text-slate-500 text-xs">{formatDate(item.created_at)}</span>
              </div>
            </div>
          )}
        />

        {/* Volunteer Applications */}
        <ActivityCard
          title="Volunteer Applications"
          icon={Heart}
          items={activity?.volunteers}
          emptyText="No recent applications"
          onViewAll={() => navigate('/admin/volunteers')}
          renderItem={(item, idx) => (
            <div key={idx} className="flex items-start justify-between gap-3 pb-3 border-b border-white/5 last:border-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm truncate">{item.name}</p>
                <p className="text-slate-500 text-xs">{item.role}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(item.status)}`}>
                  {item.status}
                </span>
                <span className="text-slate-500 text-xs">{formatDate(item.created_at)}</span>
              </div>
            </div>
          )}
        />

        {/* New Members - Full width */}
        <div className="md:col-span-2">
          <ActivityCard
            title="New Members"
            icon={UserPlus}
            items={activity?.new_members}
            emptyText="No recent members"
            onViewAll={() => navigate('/admin/users')}
            renderItem={(item, idx) => (
              <div key={idx} className="flex items-center justify-between gap-3 pb-3 border-b border-white/5 last:border-0 last:pb-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-purple/20 flex items-center justify-center text-purple-light text-sm font-medium">
                    {(item.name || item.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{item.name || 'No name'}</p>
                    <p className="text-slate-500 text-xs truncate">{item.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-500/20 text-slate-400">
                    {item.role || 'member'}
                  </span>
                  <span className="text-slate-500 text-xs whitespace-nowrap">{formatDate(item.created_at)}</span>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
}

// Sortable Item Component
function SortableItem({ item, index, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-item-${index}`}
      className={`flex items-center justify-between p-4 bg-void-surface border border-white/5 hover:border-purple/30 transition-colors ${isDragging ? 'shadow-lg shadow-purple/20' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-grow">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-purple-light p-1 touch-none"
          data-testid={`drag-handle-${index}`}
        >
          <GripVertical className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-grow">
          <h3 className="font-medium text-white truncate">
            {item.title || item.name}
          </h3>
          <p className="text-sm text-slate-400 truncate">
            {item.excerpt || item.description || item.bio || ""}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(item)}
          data-testid={`edit-btn-${index}`}
          className="text-slate-400 hover:text-white"
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(item.id)}
          data-testid={`delete-btn-${index}`}
          className="text-slate-400 hover:text-red-400"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Generic Admin CRUD Component with Drag & Drop
function AdminSection({ title, apiPath, fields, defaultItem }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState(defaultItem);
  const [submitting, setSubmitting] = useState(false);
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/${apiPath}`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      toast.error("Failed to fetch items");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    // Save new order to backend
    setReordering(true);
    try {
      const orderedIds = newItems.map((item) => item.id);
      await axios.put(`${API}/admin/${apiPath}/reorder`, { ordered_ids: orderedIds }, { withCredentials: true });
      toast.success("Order saved");
    } catch (error) {
      toast.error("Failed to save order");
      // Revert on failure
      fetchItems();
    } finally {
      setReordering(false);
    }
  };

  const handleCreate = () => {
    setEditItem(null);
    setFormData(defaultItem);
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    // Merge with defaultItem to ensure all fields have values
    const mergedData = { ...defaultItem, ...item };
    setFormData(mergedData);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    
    try {
      await axios.delete(`${API}/admin/${apiPath}/${id}`, { withCredentials: true });
      toast.success("Item deleted");
      fetchItems();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const missingFields = fields
      .filter(field => field.required && (!formData[field.name] || formData[field.name] === ""))
      .map(field => field.placeholder || field.name);
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingFields.join(", ")}`);
      return;
    }

    setSubmitting(true);
    try {
      // Clean form data - convert empty strings to null for optional fields
      const cleanedData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [
          key, 
          value === "" ? null : value
        ])
      );
      
      if (editItem) {
        await axios.put(`${API}/admin/${apiPath}/${editItem.id}`, cleanedData, { withCredentials: true });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/admin/${apiPath}`, cleanedData, { withCredentials: true });
        toast.success("Item created");
      }
      setIsModalOpen(false);
      setFormData(defaultItem);
      fetchItems();
    } catch (error) {
      console.error("Submit error:", error.response?.data || error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'object' && errorMsg !== null) {
        // Pydantic validation error format
        toast.error("Validation error: Please check all required fields");
      } else {
        toast.error(errorMsg || "Operation failed. Please check all fields.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-syne text-xl font-bold text-white">{title}</h2>
        <Button
          onClick={handleCreate}
          data-testid="admin-create-btn"
          className="bg-purple hover:bg-purple-hover text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-void-surface border border-white/5">
          <p className="text-slate-400">No items yet. Create your first one!</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {reordering && (
                <div className="flex items-center gap-2 text-purple-light text-sm mb-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving order...
                </div>
              )}
              {items.map((item, index) => (
                <SortableItem
                  key={item.id}
                  item={item}
                  index={index}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Edit/Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">
              {editItem ? "Edit" : "Create"} {title.slice(0, -1)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {fields.map((field) => (
              <div key={field.name}>
                <label className="block text-xs text-slate-500 mb-1">
                  {field.label || field.placeholder || field.name}
                  {field.required && <span className="text-red-400 ml-1">*</span>}
                </label>
                {field.type === "textarea" ? (
                  <Textarea
                    value={formData[field.name] || ""}
                    onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                    placeholder={field.placeholder || ""}
                    rows={4}
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                ) : field.type === "select" ? (
                  <select
                    value={formData[field.name] || field.options[0] || ""}
                    onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                    className="w-full px-3 py-2 bg-void border border-white/10 text-white"
                  >
                    {field.options.map((opt) => (
                      <option key={opt} value={opt} className="bg-void-surface">
                        {opt.includes('/') ? opt.replace(/_/g, ' ') : opt}
                      </option>
                    ))}
                  </select>
                ) : field.type === "checkbox" ? (
                  <input
                    type="checkbox"
                    checked={formData[field.name] || false}
                    onChange={(e) => setFormData({...formData, [field.name]: e.target.checked})}
                    className="w-5 h-5"
                  />
                ) : (
                  <Input
                    type={field.type || "text"}
                    value={formData[field.name] ?? ""}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (field.type === "number") {
                        value = value === "" ? null : parseInt(value, 10);
                        if (isNaN(value)) value = null;
                      }
                      setFormData({...formData, [field.name]: value});
                    }}
                    placeholder={field.placeholder || ""}
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                )}
              </div>
            ))}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-purple hover:bg-purple-hover text-white flex-grow"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editItem ? "Update" : "Create"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Section Components
function AdminNews() {
  return (
    <AdminSection
      title="News Articles"
      apiPath="news"
      defaultItem={{ title: "", excerpt: "", content: "", image_url: "", video_url: "", author: "", category: "general", published: true }}
      fields={[
        { name: "title", placeholder: "Article title", required: true },
        { name: "excerpt", type: "textarea", placeholder: "Brief summary", required: true },
        { name: "content", type: "textarea", placeholder: "Full article content. URLs become clickable links. Put YouTube/Vimeo URLs on their own line to embed videos.", required: true },
        { name: "image_url", placeholder: "Featured image URL (optional)" },
        { name: "video_url", placeholder: "Featured video URL - YouTube or Vimeo (optional)" },
        { name: "author", placeholder: "Author name", required: true },
        { name: "category", type: "select", options: ["general", "industry", "technology", "festivals", "interviews", "reviews"] },
        { name: "published", type: "checkbox", label: "Published" },
      ]}
    />
  );
}

function AdminFestivals() {
  return (
    <AdminSection
      title="Festivals"
      apiPath="festivals"
      defaultItem={{ name: "", description: "", location: "", start_date: "", end_date: "", image_url: "", website_url: "", submission_deadline: "", coverage: "" }}
      fields={[
        { name: "name", placeholder: "Festival name", required: true },
        { name: "description", type: "textarea", placeholder: "Description (optional, supports line breaks)" },
        { name: "location", placeholder: "City, Country", required: true },
        { name: "start_date", type: "date", placeholder: "Start date", required: true },
        { name: "end_date", type: "date", placeholder: "End date (optional for single-day)" },
        { name: "image_url", placeholder: "Image URL (optional)" },
        { name: "website_url", placeholder: "Website URL" },
        { name: "submission_deadline", type: "date", placeholder: "Submission deadline" },
        { name: "coverage", type: "textarea", placeholder: "Festival coverage/recap" },
      ]}
    />
  );
}

function AdminDirectors() {
  return (
    <AdminSection
      title="Directors"
      apiPath="directors"
      defaultItem={{ name: "", bio: "", image_url: "", website_url: "", interview_url: "", films: "", twitter: "", instagram: "", youtube: "", vimeo: "" }}
      fields={[
        { name: "name", placeholder: "Director name", required: true },
        { name: "bio", type: "textarea", placeholder: "Biography", required: true },
        { name: "image_url", placeholder: "Profile image URL" },
        { name: "website_url", placeholder: "Website URL" },
        { name: "films", type: "textarea", placeholder: "Filmography (one film per line)", label: "Filmography" },
        { name: "twitter", placeholder: "Twitter/X handle (e.g., @username)" },
        { name: "instagram", placeholder: "Instagram handle (e.g., @username)" },
        { name: "youtube", placeholder: "YouTube channel URL" },
        { name: "vimeo", placeholder: "Vimeo profile URL" },
        { name: "interview_url", placeholder: "Interview video URL" },
      ]}
    />
  );
}

// Resource Lists Admin Component (new list-based format)
function AdminResourceLists() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    icon: "video-gen",
    items: []
  });
  const [newItemName, setNewItemName] = useState("");
  const [newItemUrl, setNewItemUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/resource-categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      toast.error("Failed to fetch resource categories");
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!newItemName.trim() || !newItemUrl.trim()) {
      toast.error("Please enter both name and URL");
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { name: newItemName.trim(), url: newItemUrl.trim() }]
    }));
    setNewItemName("");
    setNewItemUrl("");
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleEdit = (category) => {
    setEditCategory(category);
    setFormData({
      title: category.title,
      icon: category.icon,
      items: category.items || []
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/admin/resource-categories/${id}`, { withCredentials: true });
      toast.success("Deleted successfully");
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Please enter a category title");
      return;
    }

    setSubmitting(true);
    try {
      if (editCategory) {
        await axios.put(`${API}/admin/resource-categories/${editCategory.id}`, formData, { withCredentials: true });
        toast.success("Updated successfully");
      } else {
        await axios.post(`${API}/admin/resource-categories`, formData, { withCredentials: true });
        toast.success("Created successfully");
      }
      setIsModalOpen(false);
      setEditCategory(null);
      setFormData({ title: "", icon: "video-gen", items: [] });
      fetchCategories();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const iconOptions = [
    { value: "video-gen", label: "Video Generation" },
    { value: "image-gen", label: "Image Generation" },
    { value: "learning", label: "Learning" },
    { value: "studios", label: "Studios" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-syne text-xl font-bold text-white">Resource Lists</h2>
        <Button
          onClick={() => {
            setEditCategory(null);
            setFormData({ title: "", icon: "video-gen", items: [] });
            setIsModalOpen(true);
          }}
          data-testid="admin-create-btn"
          className="bg-purple hover:bg-purple-hover text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
      </div>

      <div className="space-y-4">
        {categories.map((category, index) => (
          <div
            key={category.id}
            className="p-4 bg-void-surface border border-white/5 hover:border-purple/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-medium text-white">{category.title}</h3>
                <p className="text-sm text-slate-500">{category.items?.length || 0} items</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(category)}
                  className="text-slate-400 hover:text-white"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(category.id)}
                  className="text-slate-400 hover:text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {category.items?.slice(0, 5).map((item, i) => (
                <span key={i} className="px-2 py-1 text-xs bg-void text-slate-400">
                  {item.name}
                </span>
              ))}
              {category.items?.length > 5 && (
                <span className="px-2 py-1 text-xs text-slate-500">
                  +{category.items.length - 5} more
                </span>
              )}
            </div>
          </div>
        ))}

        {categories.length === 0 && (
          <p className="text-slate-500 text-center py-8">No resource categories yet. Add one to get started.</p>
        )}
      </div>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">
              {editCategory ? "Edit Category" : "Add Category"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Category Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Video Generation"
                className="bg-void border-white/10 text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1">Icon Type</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="w-full p-2 bg-void border border-white/10 text-white"
              >
                {iconOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">Items</label>
              
              {/* Current items */}
              <div className="space-y-2 mb-4">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-void border border-white/10">
                    <div className="flex-grow min-w-0">
                      <p className="text-sm text-white truncate">{item.name}</p>
                      <p className="text-xs text-slate-500 truncate">{item.url}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveItem(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Add new item */}
              <div className="flex gap-2">
                <Input
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Tool name"
                  className="bg-void border-white/10 text-white flex-1"
                />
                <Input
                  value={newItemUrl}
                  onChange={(e) => setNewItemUrl(e.target.value)}
                  placeholder="URL"
                  className="bg-void border-white/10 text-white flex-1"
                />
                <Button
                  type="button"
                  onClick={handleAddItem}
                  className="bg-purple/20 hover:bg-purple/30 text-purple-light"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="border-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-purple hover:bg-purple-hover"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {editCategory ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminWorkshops() {
  return (
    <AdminSection
      title="Workshops"
      apiPath="workshops"
      defaultItem={{ title: "", description: "", instructor: "", date: "", time: "", duration: "", location: "", price: "", is_online: true, replay_url: "", is_sponsored: false, sponsor_name: "", registration_url: "" }}
      fields={[
        { name: "title", placeholder: "Workshop title", required: true },
        { name: "description", type: "textarea", placeholder: "Description (supports line breaks)", required: true },
        { name: "instructor", placeholder: "Instructor name", required: true },
        { name: "date", type: "date", placeholder: "Date", required: true },
        { name: "time", placeholder: "Time (e.g., 2:00 PM EST)" },
        { name: "duration", placeholder: "Duration (e.g., 2 hours)" },
        { name: "price", placeholder: "Price (e.g., Free, $49, $99)" },
        { name: "location", placeholder: "Location or 'Online'" },
        { name: "is_online", type: "checkbox", label: "Online Workshop" },
        { name: "replay_url", placeholder: "Replay video URL" },
        { name: "registration_url", placeholder: "Registration URL" },
        { name: "is_sponsored", type: "checkbox", label: "Sponsored" },
        { name: "sponsor_name", placeholder: "Sponsor name" },
      ]}
    />
  );
}

function AdminFilms() {
  return (
    <AdminSection
      title="Films"
      apiPath="films"
      defaultItem={{ title: "", description: "", director: "", year: new Date().getFullYear(), genre: "", duration: "", poster_url: "", trailer_url: "", watch_url: "", festival_winner: "" }}
      fields={[
        { name: "title", placeholder: "Film title", required: true },
        { name: "description", type: "textarea", placeholder: "Synopsis", required: true },
        { name: "director", placeholder: "Director name", required: true },
        { name: "year", type: "number", placeholder: "Release year" },
        { name: "genre", type: "select", options: ["Drama", "Sci-Fi", "Documentary", "Animation", "Experimental", "Horror", "Comedy", "Music Video", "Thriller", "Action", "Western", "Adventure"], required: true },
        { name: "duration", placeholder: "Duration (e.g., 12 min)", required: true },
        { name: "poster_url", placeholder: "Poster image URL (optional)" },
        { name: "trailer_url", placeholder: "Trailer URL (optional)" },
        { name: "watch_url", placeholder: "Watch URL (optional)" },
        { name: "festival_winner", placeholder: "Festival winner (optional)" },
      ]}
    />
  );
}

function AdminEvents() {
  return (
    <AdminSection
      title="Events"
      apiPath="events"
      defaultItem={{ title: "", description: "", event_type: "screening", date: "", time: "", timezone: "America/New_York", location: "", registration_url: "" }}
      fields={[
        { name: "title", placeholder: "Event title", required: true },
        { name: "description", type: "textarea", placeholder: "Description", required: true },
        { name: "event_type", type: "select", options: ["screening", "workshop", "meetup", "industry"], required: true },
        { name: "date", type: "date", placeholder: "Date", required: true },
        { name: "time", type: "time", placeholder: "Time", required: true },
        { name: "timezone", type: "select", label: "Timezone", options: [
          "America/New_York",
          "America/Chicago",
          "America/Denver",
          "America/Los_Angeles",
          "America/Anchorage",
          "Pacific/Honolulu",
          "Europe/London",
          "Europe/Paris",
          "Europe/Berlin",
          "Europe/Moscow",
          "Asia/Dubai",
          "Asia/Mumbai",
          "Asia/Singapore",
          "Asia/Tokyo",
          "Asia/Shanghai",
          "Australia/Sydney",
          "Pacific/Auckland",
          "UTC"
        ]},
        { name: "location", placeholder: "Location (City, Venue)", required: true },
        { name: "registration_url", placeholder: "Registration URL (optional)" },
      ]}
    />
  );
}

// Submissions Admin (Approve/Reject)
function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [publishForm, setPublishForm] = useState({
    title: "",
    director: "",
    year: new Date().getFullYear(),
    genre: "",
    description: "",
    video_url: "",
    poster_url: "",
    duration: "",
    award: "",
    festival_winner: false
  });
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, [filter]);

  const fetchSubmissions = async () => {
    try {
      const url = filter ? `${API}/admin/submissions?status=${filter}` : `${API}/admin/submissions`;
      const response = await axios.get(url, { withCredentials: true });
      setSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to fetch submissions");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/submissions/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Submission ${status}`);
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, { withCredentials: true });
      toast.success("Submission deleted");
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const openPublishModal = (submission) => {
    setSelectedSubmission(submission);
    setPublishForm({
      title: submission.title || "",
      director: submission.user_name || "",
      year: new Date().getFullYear(),
      genre: submission.genre || "",
      description: submission.description || "",
      video_url: submission.video_url || "",
      poster_url: "",
      duration: submission.runtime || "",
      award: "",
      festival_winner: false
    });
    setPublishModalOpen(true);
  };

  const handlePublishToGallery = async () => {
    if (!publishForm.title || !publishForm.director || !publishForm.video_url) {
      toast.error("Please fill in required fields (Title, Director, Video URL)");
      return;
    }

    setPublishing(true);
    try {
      // Create the film in the gallery
      await axios.post(`${API}/admin/submissions/${selectedSubmission.id}/publish`, publishForm, { 
        withCredentials: true 
      });
      
      toast.success("Film published to gallery!");
      setPublishModalOpen(false);
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish film");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-syne text-xl font-bold text-white">Film Submissions</h2>
        <div className="flex gap-2">
          {["pending", "approved", "published", "rejected", ""].map((f) => (
            <button
              key={f || "all"}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-sm transition-colors ${
                filter === f ? "bg-purple text-white" : "bg-void-surface text-slate-400 hover:text-white"
              }`}
            >
              {f || "All"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple animate-spin" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 bg-void-surface border border-white/5">
          <p className="text-slate-400">No submissions found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map((sub) => (
            <div
              key={sub.id}
              className="p-4 bg-void-surface border border-white/5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-white">{sub.title}</h3>
                    <span className={`px-2 py-0.5 text-xs ${
                      sub.status === "published" ? "bg-purple/20 text-purple-light" :
                      sub.status === "approved" ? "bg-green-500/20 text-green-400" :
                      sub.status === "rejected" ? "bg-red-500/20 text-red-400" :
                      "bg-yellow-500/20 text-yellow-400"
                    }`}>
                      {sub.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400">{sub.genre} • by {sub.user_name}</p>
                  <p className="text-sm text-slate-500 mt-1">{sub.description?.slice(0, 100)}...</p>
                  <a href={sub.video_url} target="_blank" rel="noreferrer" className="text-sm text-purple-light hover:text-white mt-1 inline-block">
                    View Film →
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  {sub.status === "pending" && (
                    <>
                      <Button 
                        size="sm" 
                        onClick={() => openPublishModal(sub)} 
                        className="bg-purple hover:bg-purple-hover text-white"
                        title="Publish to Gallery"
                      >
                        <Film className="w-4 h-4 mr-1" />
                        Publish
                      </Button>
                      <Button size="sm" onClick={() => updateStatus(sub.id, "rejected")} className="bg-red-600 hover:bg-red-700 text-white">
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {sub.status === "approved" && (
                    <Button 
                      size="sm" 
                      onClick={() => openPublishModal(sub)} 
                      className="bg-purple hover:bg-purple-hover text-white"
                    >
                      <Film className="w-4 h-4 mr-1" />
                      Publish
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => deleteSubmission(sub.id)} className="text-slate-400 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Publish to Gallery Modal */}
      <Dialog open={publishModalOpen} onOpenChange={setPublishModalOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">Publish to Films Gallery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Title *</label>
              <Input
                value={publishForm.title}
                onChange={(e) => setPublishForm({ ...publishForm, title: e.target.value })}
                className="bg-void border-white/10 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Director *</label>
                <Input
                  value={publishForm.director}
                  onChange={(e) => setPublishForm({ ...publishForm, director: e.target.value })}
                  className="bg-void border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Year</label>
                <Input
                  type="number"
                  value={publishForm.year}
                  onChange={(e) => setPublishForm({ ...publishForm, year: parseInt(e.target.value) })}
                  className="bg-void border-white/10 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Genre</label>
                <Input
                  value={publishForm.genre}
                  onChange={(e) => setPublishForm({ ...publishForm, genre: e.target.value })}
                  className="bg-void border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Duration</label>
                <Input
                  value={publishForm.duration}
                  onChange={(e) => setPublishForm({ ...publishForm, duration: e.target.value })}
                  placeholder="e.g., 5:30"
                  className="bg-void border-white/10 text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Video URL *</label>
              <Input
                value={publishForm.video_url}
                onChange={(e) => setPublishForm({ ...publishForm, video_url: e.target.value })}
                className="bg-void border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Poster Image URL (optional)</label>
              <Input
                value={publishForm.poster_url}
                onChange={(e) => setPublishForm({ ...publishForm, poster_url: e.target.value })}
                placeholder="https://... (leave empty for default)"
                className="bg-void border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Description</label>
              <Textarea
                value={publishForm.description}
                onChange={(e) => setPublishForm({ ...publishForm, description: e.target.value })}
                rows={3}
                className="bg-void border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Award (optional)</label>
              <Input
                value={publishForm.award}
                onChange={(e) => setPublishForm({ ...publishForm, award: e.target.value })}
                placeholder="e.g., Best Short Film"
                className="bg-void border-white/10 text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="festival_winner"
                checked={publishForm.festival_winner}
                onChange={(e) => setPublishForm({ ...publishForm, festival_winner: e.target.checked })}
                className="rounded bg-void border-white/20"
              />
              <label htmlFor="festival_winner" className="text-sm text-slate-300">
                Festival Winner
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button variant="ghost" onClick={() => setPublishModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePublishToGallery} 
                disabled={publishing}
                className="bg-purple hover:bg-purple-hover text-white"
              >
                {publishing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Film className="w-4 h-4 mr-2" />
                    Publish to Gallery
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Job Form Component (defined outside to prevent re-renders)
const JobFormFields = ({ formData, setFormData, jobTypes }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm text-slate-400 mb-1">Title *</label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="e.g., Senior AI Engineer"
          className="bg-void border-white/10 text-white"
        />
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-1">Company *</label>
        <Input
          value={formData.company}
          onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
          placeholder="Company name"
          className="bg-void border-white/10 text-white"
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm text-slate-400 mb-1">Location</label>
        <Input
          value={formData.location}
          onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
          placeholder="e.g., Remote, Los Angeles, CA"
          className="bg-void border-white/10 text-white"
        />
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-1">Job Type</label>
        <select
          value={formData.job_type}
          onChange={(e) => setFormData(prev => ({ ...prev, job_type: e.target.value }))}
          className="w-full px-3 py-2 bg-void border border-white/10 rounded-md text-white"
        >
          {jobTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>
    </div>
    <div>
      <label className="block text-sm text-slate-400 mb-1">Description *</label>
      <Textarea
        value={formData.description}
        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
        placeholder="Job description..."
        rows={4}
        className="bg-void border-white/10 text-white"
      />
    </div>
    <div>
      <label className="block text-sm text-slate-400 mb-1">Requirements</label>
      <Textarea
        value={formData.requirements}
        onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
        placeholder="Required skills and qualifications..."
        rows={3}
        className="bg-void border-white/10 text-white"
      />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm text-slate-400 mb-1">Salary Range</label>
        <Input
          value={formData.salary_range}
          onChange={(e) => setFormData(prev => ({ ...prev, salary_range: e.target.value }))}
          placeholder="e.g., $80k - $120k"
          className="bg-void border-white/10 text-white"
        />
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-1">Status</label>
        <select
          value={formData.status}
          onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
          className="w-full px-3 py-2 bg-void border border-white/10 rounded-md text-white"
        >
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm text-slate-400 mb-1">Application URL</label>
        <Input
          value={formData.application_url}
          onChange={(e) => setFormData(prev => ({ ...prev, application_url: e.target.value }))}
          placeholder="https://..."
          className="bg-void border-white/10 text-white"
        />
      </div>
      <div>
        <label className="block text-sm text-slate-400 mb-1">Contact Email</label>
        <Input
          value={formData.contact_email}
          onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
          placeholder="jobs@company.com"
          className="bg-void border-white/10 text-white"
        />
      </div>
    </div>
  </div>
);

// Jobs Admin (Approve/Reject/Create/Edit)
function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    location: "",
    job_type: "full-time",
    description: "",
    requirements: "",
    salary_range: "",
    application_url: "",
    contact_email: "",
    status: "approved"
  });

  const jobTypes = ["full-time", "part-time", "contract", "freelance", "internship"];

  useEffect(() => {
    fetchJobs();
  }, [filter]);

  const fetchJobs = async () => {
    try {
      const url = filter ? `${API}/admin/jobs?status=${filter}` : `${API}/admin/jobs`;
      const response = await axios.get(url, { withCredentials: true });
      setJobs(response.data);
    } catch (error) {
      toast.error("Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/jobs/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Job ${status}`);
      fetchJobs();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this job posting?")) return;
    try {
      await axios.delete(`${API}/admin/jobs/${id}`, { withCredentials: true });
      toast.success("Job deleted");
      fetchJobs();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.company || !formData.description) {
      toast.error("Please fill in required fields (Title, Company, Description)");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/jobs`, formData, { withCredentials: true });
      toast.success("Job created successfully");
      setIsCreateOpen(false);
      setFormData({
        title: "", company: "", location: "", job_type: "full-time",
        description: "", requirements: "", salary_range: "",
        application_url: "", contact_email: "", status: "approved"
      });
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!formData.title || !formData.company || !formData.description) {
      toast.error("Please fill in required fields");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/jobs/${selectedJob.id}`, formData, { withCredentials: true });
      toast.success("Job updated successfully");
      setIsEditOpen(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update job");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (job) => {
    setSelectedJob(job);
    setFormData({
      title: job.title || "",
      company: job.company || "",
      location: job.location || "",
      job_type: job.job_type || "full-time",
      description: job.description || "",
      requirements: job.requirements || "",
      salary_range: job.salary_range || "",
      application_url: job.application_url || "",
      contact_email: job.contact_email || "",
      status: job.status || "pending"
    });
    setIsEditOpen(true);
  };

  const openView = (job) => {
    setSelectedJob(job);
    setIsViewOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-syne text-xl font-bold text-white">Job Postings</h2>
          <p className="text-sm text-slate-400 mt-1">{jobs.length} jobs</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {["pending", "approved", "rejected", ""].map((f) => (
              <button
                key={f || "all"}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 text-sm transition-colors ${
                  filter === f ? "bg-purple text-white" : "bg-void-surface text-slate-400 hover:text-white"
                }`}
              >
                {f || "All"}
              </button>
            ))}
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="bg-purple hover:bg-purple-light">
            <Plus className="w-4 h-4 mr-2" />
            Create Job
          </Button>
        </div>
      </div>

      {/* Create Job Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">Create Job Posting</DialogTitle>
          </DialogHeader>
          <JobFormFields formData={formData} setFormData={setFormData} jobTypes={jobTypes} />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="border-white/10 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-purple hover:bg-purple-light">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Job
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">Edit Job Posting</DialogTitle>
          </DialogHeader>
          <JobFormFields formData={formData} setFormData={setFormData} jobTypes={jobTypes} />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setIsEditOpen(false)} className="border-white/10 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving} className="bg-purple hover:bg-purple-light">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Job Details Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">{selectedJob?.title}</DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Company:</span>
                  <span className="ml-2 text-white">{selectedJob.company}</span>
                </div>
                <div>
                  <span className="text-slate-400">Location:</span>
                  <span className="ml-2 text-white">{selectedJob.location || "Not specified"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Type:</span>
                  <span className="ml-2 text-white">{selectedJob.job_type}</span>
                </div>
                <div>
                  <span className="text-slate-400">Salary:</span>
                  <span className="ml-2 text-white">{selectedJob.salary_range || "Not specified"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Status:</span>
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                    selectedJob.status === "approved" ? "bg-green-500/20 text-green-400" :
                    selectedJob.status === "rejected" ? "bg-red-500/20 text-red-400" :
                    "bg-yellow-500/20 text-yellow-400"
                  }`}>{selectedJob.status}</span>
                </div>
                <div>
                  <span className="text-slate-400">Posted by:</span>
                  <span className="ml-2 text-white">{selectedJob.user_name}</span>
                </div>
              </div>
              <div>
                <h4 className="text-slate-400 text-sm mb-2">Description</h4>
                <p className="text-white text-sm whitespace-pre-wrap bg-void p-3 rounded">{selectedJob.description}</p>
              </div>
              {selectedJob.requirements && (
                <div>
                  <h4 className="text-slate-400 text-sm mb-2">Requirements</h4>
                  <p className="text-white text-sm whitespace-pre-wrap bg-void p-3 rounded">{selectedJob.requirements}</p>
                </div>
              )}
              {(selectedJob.application_url || selectedJob.contact_email) && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedJob.application_url && (
                    <div>
                      <span className="text-slate-400">Apply URL:</span>
                      <a href={selectedJob.application_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-purple-light hover:underline">
                        {selectedJob.application_url.slice(0, 30)}...
                      </a>
                    </div>
                  )}
                  {selectedJob.contact_email && (
                    <div>
                      <span className="text-slate-400">Contact:</span>
                      <span className="ml-2 text-white">{selectedJob.contact_email}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button
                  variant="outline"
                  onClick={() => { setIsViewOpen(false); openEdit(selectedJob); }}
                  className="border-white/10 text-slate-300"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                {selectedJob.status === "pending" && (
                  <>
                    <Button
                      onClick={() => { updateStatus(selectedJob.id, "approved"); setIsViewOpen(false); }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => { updateStatus(selectedJob.id, "rejected"); setIsViewOpen(false); }}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple animate-spin" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 bg-void-surface border border-white/5">
          <p className="text-slate-400">No job postings found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job, index) => (
            <div
              key={job.id}
              className="p-4 bg-void-surface border border-white/5 hover:border-purple/20 transition-colors cursor-pointer"
              onClick={() => openView(job)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-white">{job.title}</h3>
                    <span className={`px-2 py-0.5 text-xs ${
                      job.status === "approved" ? "bg-green-500/20 text-green-400" :
                      job.status === "rejected" ? "bg-red-500/20 text-red-400" :
                      "bg-yellow-500/20 text-yellow-400"
                    }`}>
                      {job.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400">{job.company} • {job.location} • {job.job_type}</p>
                  <p className="text-sm text-slate-500 mt-1">{job.description?.slice(0, 100)}...</p>
                  <p className="text-xs text-slate-600 mt-1">Posted by: {job.user_name}</p>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(job)} className="text-slate-400 hover:text-purple-light">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {job.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => updateStatus(job.id, "approved")} className="bg-green-600 hover:bg-green-700 text-white">
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={() => updateStatus(job.id, "rejected")} className="bg-red-600 hover:bg-red-700 text-white">
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => deleteJob(job.id)} className="text-slate-400 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// User Management Admin
function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      // The endpoint caps each response at 200, so page through until we have
      // everyone. Requesting a single page left users past the first 200
      // invisible here — unmanageable, and the "Total Users" count wrong.
      const PAGE_SIZE = 200;
      let all = [];
      for (let skip = 0; ; skip += PAGE_SIZE) {
        const { data } = await axios.get(
          `${API}/admin/users?skip=${skip}&limit=${PAGE_SIZE}`,
          { withCredentials: true }
        );
        all = all.concat(data.users);
        if (data.users.length < PAGE_SIZE || all.length >= data.total) break;
      }
      setUsers(all);
    } catch (error) {
      toast.error("Failed to fetch users. You may need Super Admin access.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/admin/roles`, { withCredentials: true });
      setRoles(response.data.roles);
    } catch (error) {
      console.error("Failed to fetch roles");
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.role || "user");
    setIsModalOpen(true);
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const openPasswordModal = (user) => {
    setUserToResetPassword(user);
    setNewPassword("");
    setConfirmPassword("");
    setIsPasswordModalOpen(true);
  };

  const updateUserRole = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}/role`, { role: newRole }, { withCredentials: true });
      toast.success(`${selectedUser.name}'s role updated to ${newRole}`);
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const resetUserPassword = async () => {
    if (!userToResetPassword) return;
    
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    setResettingPassword(true);
    try {
      await axios.put(
        `${API}/admin/users/${userToResetPassword.user_id}/password`, 
        { new_password: newPassword }, 
        { withCredentials: true }
      );
      toast.success(`Password reset for ${userToResetPassword.name}`);
      setIsPasswordModalOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.user_id}`, { withCredentials: true });
      toast.success(`${userToDelete.name} has been deleted`);
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "super_admin": return "bg-red-500/20 text-red-400";
      case "admin": return "bg-purple/20 text-purple-light";
      case "content_moderator": return "bg-blue-500/20 text-blue-400";
      case "event_manager": return "bg-green-500/20 text-green-400";
      case "content_editor": return "bg-yellow-500/20 text-yellow-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  const formatRoleName = (role) => {
    return role?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "User";
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-syne text-xl font-bold text-white">User Management</h2>
          <p className="text-sm text-slate-400 mt-1">Assign roles to team members and volunteers</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              const token = localStorage.getItem("aifilm_session_token");
              const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
              window.open(`${backendUrl}/api/admin/users/export-csv?token=${token}`, "_blank");
            }}
            className="px-4 py-2 bg-purple-light/20 text-purple-light border border-purple-light/30 hover:bg-purple-light/30 transition-colors text-sm"
          >
            Export Emails CSV
          </button>
          <div className="text-right">
            <span className="text-3xl font-bold text-purple-light">{users.length}</span>
            <p className="text-sm text-slate-400">Total Users</p>
          </div>
        </div>
      </div>

      {/* Role Legend */}
      <div className="mb-6 p-4 bg-void-surface border border-white/5">
        <h3 className="text-sm font-medium text-white mb-3">Role Permissions</h3>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <span className="inline-block px-2 py-1 bg-red-500/20 text-red-400 mb-1">Super Admin</span>
            <p className="text-slate-500">Full access to everything</p>
          </div>
          <div>
            <span className="inline-block px-2 py-1 bg-blue-500/20 text-blue-400 mb-1">Content Moderator</span>
            <p className="text-slate-500">Approve submissions & jobs</p>
          </div>
          <div>
            <span className="inline-block px-2 py-1 bg-green-500/20 text-green-400 mb-1">Event Manager</span>
            <p className="text-slate-500">Manage events & workshops</p>
          </div>
          <div>
            <span className="inline-block px-2 py-1 bg-yellow-500/20 text-yellow-400 mb-1">Content Editor</span>
            <p className="text-slate-500">Manage news & resources</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 bg-void-surface border border-white/5">
          <p className="text-slate-400">No users found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map((user, index) => (
            <div
              key={user.user_id}
              data-testid={`user-${index}`}
              className="flex items-center justify-between p-4 bg-void-surface border border-white/5 hover:border-purple/30 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-purple/20 flex items-center justify-center overflow-hidden">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-5 h-5 text-purple-light" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-white">{user.name}</h3>
                  <p className="text-sm text-slate-500">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 text-xs ${getRoleBadgeColor(user.role)}`}>
                  {formatRoleName(user.role)}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openRoleModal(user)}
                  className="text-slate-400 hover:text-white"
                >
                  <Shield className="w-4 h-4 mr-1" />
                  Change Role
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openPasswordModal(user)}
                  className="text-slate-400 hover:text-yellow-400"
                  title="Reset Password"
                >
                  <Key className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openDeleteModal(user)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Role Change Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">
              Change Role for {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Select Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-3 py-2 bg-void border border-white/10 text-white"
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id} className="bg-void-surface">
                    {role.name} - {role.description}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={updateUserRole}
                className="bg-purple hover:bg-purple-hover text-white flex-grow"
              >
                <Save className="w-4 h-4 mr-2" />
                Update Role
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl text-red-400">
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-300">
              Are you sure you want to delete <strong className="text-white">{userToDelete?.name}</strong>?
            </p>
            <p className="text-sm text-slate-500">
              This will permanently remove the user and all their data including:
            </p>
            <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
              <li>Profile information</li>
              <li>Film submissions</li>
              <li>Job postings</li>
              <li>Forum posts and replies</li>
              <li>Donation history</li>
            </ul>
            <p className="text-sm text-red-400 font-medium">
              This action cannot be undone.
            </p>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={deleteUser}
                className="bg-red-600 hover:bg-red-700 text-white flex-grow"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete User
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">
              Reset Password for {userToResetPassword?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              Set a new password for <strong className="text-white">{userToResetPassword?.email}</strong>
            </p>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">New Password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Confirm Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={resetUserPassword}
                disabled={resettingPassword}
                className="bg-yellow-600 hover:bg-yellow-700 text-white flex-grow"
              >
                {resettingPassword ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsPasswordModalOpen(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Volunteer Applications Admin
function AdminVolunteers() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/admin/volunteers`, { withCredentials: true });
      setApplications(response.data);
    } catch (error) {
      toast.error("Failed to fetch volunteer applications");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/volunteers/${id}`, { status }, { withCredentials: true });
      toast.success(`Application ${status}`);
      fetchApplications();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div>
      <h2 className="font-syne text-xl font-bold text-white mb-6">
        Volunteer Applications
      </h2>
      
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 text-purple animate-spin" />
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-10 text-slate-500">
          <Heart className="w-10 h-10 mx-auto mb-3 opacity-50" />
          No volunteer applications yet.
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <div key={app.id} className="p-4 bg-void-surface border border-white/5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium text-white">{app.name}</h3>
                    <span className={`px-2 py-0.5 text-xs ${
                      app.status === 'approved' 
                        ? 'bg-green-500/20 text-green-400' 
                        : app.status === 'rejected'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {app.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-400">{app.email}</p>
                  <p className="text-sm text-purple-light mt-1">Role: {app.role}</p>
                  {app.message && (
                    <p className="text-sm text-slate-500 mt-2 italic">"{app.message}"</p>
                  )}
                  <p className="text-xs text-slate-600 mt-2">
                    Applied: {new Date(app.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {app.status === 'pending' && (
                    <>
                      <Button 
                        size="sm" 
                        onClick={() => updateStatus(app.id, "approved")} 
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => updateStatus(app.id, "rejected")} 
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== FOUNDERS ADMIN (Super Admin Only) =====================
function AdminFounders() {
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editFounder, setEditFounder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    title: "Founder",
    bio: "",
    image_url: "",
    linkedin_url: "",
    twitter_url: "",
    instagram_url: "",
    youtube_url: "",
    website_url: "",
    imdb_url: "",
    display_order: 0
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchFounders();
  }, []);

  const fetchFounders = async () => {
    try {
      const response = await axios.get(`${API}/admin/founders`, { withCredentials: true });
      setFounders(response.data);
    } catch (error) {
      toast.error("Failed to fetch founders");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = founders.findIndex((f) => f.id === active.id);
    const newIndex = founders.findIndex((f) => f.id === over.id);
    
    const newFounders = arrayMove(founders, oldIndex, newIndex);
    setFounders(newFounders);

    // Save new order to backend
    setReordering(true);
    try {
      for (let i = 0; i < newFounders.length; i++) {
        await axios.put(`${API}/admin/founders/${newFounders[i].id}`, 
          { ...newFounders[i], display_order: i }, 
          { withCredentials: true }
        );
      }
      toast.success("Order saved");
    } catch (error) {
      toast.error("Failed to save order");
      fetchFounders();
    } finally {
      setReordering(false);
    }
  };

  const handleCreate = () => {
    setEditFounder(null);
    setFormData({
      name: "",
      title: "Founder",
      bio: "",
      image_url: "",
      linkedin_url: "",
      twitter_url: "",
      instagram_url: "",
      youtube_url: "",
      website_url: "",
      imdb_url: "",
      display_order: founders.length
    });
    setIsModalOpen(true);
  };

  const handleEdit = (founder) => {
    setEditFounder(founder);
    setFormData({
      name: founder.name || "",
      title: founder.title || "Founder",
      bio: founder.bio || "",
      image_url: founder.image_url || "",
      linkedin_url: founder.linkedin_url || "",
      twitter_url: founder.twitter_url || "",
      instagram_url: founder.instagram_url || "",
      youtube_url: founder.youtube_url || "",
      website_url: founder.website_url || "",
      imdb_url: founder.imdb_url || "",
      display_order: founder.display_order || 0
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this founder?")) return;
    
    try {
      await axios.delete(`${API}/admin/founders/${id}`, { withCredentials: true });
      toast.success("Founder removed");
      fetchFounders();
    } catch (error) {
      toast.error("Failed to remove founder");
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setSubmitting(true);
    try {
      const cleanedData = {
        name: formData.name.trim(),
        title: formData.title.trim() || "Founder",
        bio: formData.bio.trim() || null,
        image_url: formData.image_url.trim() || null,
        linkedin_url: formData.linkedin_url.trim() || null,
        twitter_url: formData.twitter_url.trim() || null,
        instagram_url: formData.instagram_url.trim() || null,
        youtube_url: formData.youtube_url.trim() || null,
        website_url: formData.website_url.trim() || null,
        imdb_url: formData.imdb_url.trim() || null,
        display_order: formData.display_order
      };

      if (editFounder) {
        await axios.put(`${API}/admin/founders/${editFounder.id}`, cleanedData, { withCredentials: true });
        toast.success("Founder updated");
      } else {
        await axios.post(`${API}/admin/founders`, cleanedData, { withCredentials: true });
        toast.success("Founder added");
      }
      setIsModalOpen(false);
      fetchFounders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    if (editFounder) {
      setUploadingImage(true);
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(
          `${API}/admin/founders/${editFounder.id}/picture`, 
          formDataUpload, 
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        
        setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        toast.success("Image uploaded successfully!");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to upload image");
      } finally {
        setUploadingImage(false);
      }
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image_url: reader.result }));
      };
      reader.readAsDataURL(file);
      toast.success("Image added - will be saved when you create the founder");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-syne text-xl font-bold text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-purple-light" />
            Founders
          </h2>
          <p className="text-slate-400 text-sm mt-1">Manage founders displayed on the About page</p>
        </div>
        <Button
          onClick={handleCreate}
          data-testid="admin-founder-create-btn"
          className="bg-purple hover:bg-purple-hover text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Founder
        </Button>
      </div>

      {founders.length === 0 ? (
        <div className="text-center py-12 bg-void-surface border border-white/5">
          <Crown className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No founders yet. Add your first one!</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={founders.map(f => f.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {reordering && (
                <div className="flex items-center gap-2 text-purple-light text-sm mb-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving order...
                </div>
              )}
              {founders.map((founder) => (
                <FounderItem
                  key={founder.id}
                  founder={founder}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Edit/Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">
              {editFounder ? "Edit Founder" : "Add Founder"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  Name <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Full name"
                  className="bg-void border-white/10 text-white placeholder-slate-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Founder, Co-Founder"
                  className="bg-void border-white/10 text-white placeholder-slate-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Bio</label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                placeholder="Brief biography"
                rows={4}
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Profile Image</label>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://... or upload an image"
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {formData.image_url && (
                <div className="mt-3 flex items-center gap-3">
                  <img 
                    src={formData.image_url} 
                    alt="Preview" 
                    className="w-16 h-16 rounded-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, image_url: ""})}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Remove image
                  </button>
                </div>
              )}
            </div>
            
            {/* Social Links Section */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-purple-light" />
                Website & Social Links
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <Globe className="w-3 h-3" /> Website
                  </label>
                  <Input
                    value={formData.website_url}
                    onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                    placeholder="https://..."
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <Linkedin className="w-3 h-3" /> LinkedIn
                  </label>
                  <Input
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                    placeholder="https://linkedin.com/in/..."
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <Twitter className="w-3 h-3" /> Twitter/X
                  </label>
                  <Input
                    value={formData.twitter_url}
                    onChange={(e) => setFormData({...formData, twitter_url: e.target.value})}
                    placeholder="https://twitter.com/..."
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <Instagram className="w-3 h-3" /> Instagram
                  </label>
                  <Input
                    value={formData.instagram_url}
                    onChange={(e) => setFormData({...formData, instagram_url: e.target.value})}
                    placeholder="https://instagram.com/..."
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <Youtube className="w-3 h-3" /> YouTube
                  </label>
                  <Input
                    value={formData.youtube_url}
                    onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
                    placeholder="https://youtube.com/..."
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> IMDb
                  </label>
                  <Input
                    value={formData.imdb_url}
                    onChange={(e) => setFormData({...formData, imdb_url: e.target.value})}
                    placeholder="https://imdb.com/name/..."
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-purple hover:bg-purple-hover text-white flex-grow"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editFounder ? "Update" : "Add Founder"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sortable Founder Item
function FounderItem({ founder, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: founder.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const socialLinks = [
    { url: founder.website_url, icon: Globe, label: "Website" },
    { url: founder.linkedin_url, icon: Linkedin, label: "LinkedIn" },
    { url: founder.twitter_url, icon: Twitter, label: "Twitter" },
    { url: founder.instagram_url, icon: Instagram, label: "Instagram" },
    { url: founder.youtube_url, icon: Youtube, label: "YouTube" },
    { url: founder.imdb_url, icon: ExternalLink, label: "IMDb" },
  ].filter(link => link.url);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`founder-item-${founder.id}`}
      className="p-4 bg-void-surface border border-white/5 hover:border-purple/30 transition-colors"
    >
      <div className="flex items-center gap-4">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-white"
        >
          <GripVertical className="w-5 h-5" />
        </button>
        
        {founder.image_url ? (
          <img 
            src={founder.image_url} 
            alt={founder.name}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-purple/20 flex items-center justify-center flex-shrink-0">
            <Crown className="w-6 h-6 text-purple-light" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-white truncate">{founder.name}</h3>
          <p className="text-purple-light text-sm">{founder.title}</p>
          {socialLinks.length > 0 && (
            <div className="flex items-center gap-2 mt-1">
              {socialLinks.map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-500 hover:text-purple-light transition-colors"
                  title={link.label}
                >
                  <link.icon className="w-3 h-3" />
                </a>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(founder)}
            className="text-slate-400 hover:text-white"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(founder.id)}
            className="text-slate-400 hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Board & Advisors Admin Component (Super Admin Only)
function AdminBoardMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    title: "",
    bio: "",
    image_url: "",
    linkedin_url: "",
    website_url: "",
    display_order: 0
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/board-members`, { withCredentials: true });
      setMembers(response.data);
    } catch (error) {
      toast.error("Failed to fetch board members");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = members.findIndex((m) => m.id === active.id);
    const newIndex = members.findIndex((m) => m.id === over.id);
    
    const newMembers = arrayMove(members, oldIndex, newIndex);
    setMembers(newMembers);

    // Save new order to backend
    setReordering(true);
    try {
      // Update display_order for each member
      for (let i = 0; i < newMembers.length; i++) {
        await axios.put(`${API}/admin/board-members/${newMembers[i].id}`, 
          { ...newMembers[i], display_order: i }, 
          { withCredentials: true }
        );
      }
      toast.success("Order saved");
    } catch (error) {
      toast.error("Failed to save order");
      fetchMembers();
    } finally {
      setReordering(false);
    }
  };

  const handleCreate = () => {
    setEditMember(null);
    setFormData({
      name: "",
      role: "",
      title: "",
      bio: "",
      image_url: "",
      linkedin_url: "",
      website_url: "",
      display_order: members.length
    });
    setIsModalOpen(true);
  };

  const handleEdit = (member) => {
    setEditMember(member);
    setFormData({
      name: member.name || "",
      role: member.role || "",
      title: member.title || "",
      bio: member.bio || "",
      image_url: member.image_url || "",
      linkedin_url: member.linkedin_url || "",
      website_url: member.website_url || "",
      display_order: member.display_order || 0
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this board member?")) return;
    
    try {
      await axios.delete(`${API}/admin/board-members/${id}`, { withCredentials: true });
      toast.success("Board member removed");
      fetchMembers();
    } catch (error) {
      toast.error("Failed to remove board member");
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.role.trim()) {
      toast.error("Name and role are required");
      return;
    }

    setSubmitting(true);
    try {
      const cleanedData = {
        name: formData.name.trim(),
        role: formData.role.trim(),
        title: formData.title.trim() || null,
        bio: formData.bio.trim() || null,
        image_url: formData.image_url.trim() || null,
        linkedin_url: formData.linkedin_url.trim() || null,
        website_url: formData.website_url.trim() || null,
        display_order: formData.display_order
      };

      if (editMember) {
        await axios.put(`${API}/admin/board-members/${editMember.id}`, cleanedData, { withCredentials: true });
        toast.success("Board member updated");
      } else {
        await axios.post(`${API}/admin/board-members`, cleanedData, { withCredentials: true });
        toast.success("Board member added");
      }
      setIsModalOpen(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    // If editing existing member, upload directly
    if (editMember) {
      setUploadingImage(true);
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(
          `${API}/admin/board-members/${editMember.id}/picture`, 
          formDataUpload, 
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        
        setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        toast.success("Image uploaded successfully!");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to upload image");
      } finally {
        setUploadingImage(false);
      }
    } else {
      // For new members, convert to base64 preview (will be saved when member is created)
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image_url: reader.result }));
      };
      reader.readAsDataURL(file);
      toast.success("Image added - will be saved when you create the member");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-syne text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-light" />
            Board & Advisors
          </h2>
          <p className="text-slate-400 text-sm mt-1">Manage board members and advisors displayed on the About page</p>
        </div>
        <Button
          onClick={handleCreate}
          data-testid="admin-board-create-btn"
          className="bg-purple hover:bg-purple-hover text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-12 bg-void-surface border border-white/5">
          <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No board members yet. Add your first one!</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={members.map(m => m.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {reordering && (
                <div className="flex items-center gap-2 text-purple-light text-sm mb-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving order...
                </div>
              )}
              {members.map((member) => (
                <BoardMemberItem
                  key={member.id}
                  member={member}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Edit/Create Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-void-surface border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-syne text-xl">
              {editMember ? "Edit Board Member" : "Add Board Member"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Name <span className="text-red-400">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Full name"
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Role <span className="text-red-400">*</span>
              </label>
              <Input
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                placeholder="e.g., Founding Advisor, Board Member, Advisory Board"
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Title / Specialty</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., CGI and Motion, AI Research"
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Bio</label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                placeholder="Brief biography"
                rows={3}
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Profile Image</label>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://... or upload an image"
                    className="bg-void border-white/10 text-white placeholder-slate-500"
                  />
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {formData.image_url && (
                <div className="mt-3 flex items-center gap-3">
                  <img 
                    src={formData.image_url} 
                    alt="Preview" 
                    className="w-16 h-16 rounded-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, image_url: ""})}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Remove image
                  </button>
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">Upload an image (max 5MB) or paste a URL</p>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">LinkedIn URL</label>
              <Input
                value={formData.linkedin_url}
                onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                placeholder="https://linkedin.com/in/..."
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Website URL</label>
              <Input
                value={formData.website_url}
                onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                placeholder="https://..."
                className="bg-void border-white/10 text-white placeholder-slate-500"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-purple hover:bg-purple-hover text-white flex-grow"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editMember ? "Update" : "Add Member"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sortable Board Member Item
function BoardMemberItem({ member, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: member.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`board-member-item-${member.id}`}
      className="p-4 bg-void-surface border border-white/5 hover:border-purple/30 transition-colors"
    >
      <div className="flex items-center gap-4">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-white"
        >
          <GripVertical className="w-5 h-5" />
        </button>
        
        {member.image_url ? (
          <img 
            src={member.image_url} 
            alt={member.name}
            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-purple/20 flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 text-purple-light" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-white truncate">{member.name}</h3>
          <p className="text-purple-light text-sm">{member.role}</p>
          {member.title && <p className="text-slate-500 text-xs">{member.title}</p>}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(member)}
            className="text-slate-400 hover:text-white"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(member.id)}
            className="text-slate-400 hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Site Content CMS Admin Component (Super Admin Only)
function AdminSiteContent() {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editContent, setEditContent] = useState({});

  // Define editable pages and their sections
  const editablePages = [
    {
      page: "join",
      name: "Join / Membership Page",
      sections: [
        {
          section: "header",
          name: "Page Header",
          fields: [
            { key: "headline", label: "Headline", type: "text", default: "Become a Member" },
            { key: "subhead", label: "Subhead", type: "textarea", default: "Join the global community of AI filmmakers. The Society is a network of pioneers, creators, and storytellers defining the next era of cinema." }
          ]
        },
        {
          section: "membership_card",
          name: "Membership Card",
          fields: [
            { key: "title", label: "Title", type: "text", default: "Society Membership" },
            { key: "subtitle", label: "Subtitle", type: "text", default: "Open Access" },
            { key: "description", label: "Description", type: "text", default: "Help define the future of film." },
            { key: "button_text", label: "Button Text", type: "text", default: "Become a Member" }
          ]
        },
        {
          section: "benefits_list",
          name: "Membership Benefits (Checkmark List)",
          fields: [
            { key: "benefits", label: "Benefits (one per line)", type: "benefits_list", default: [
              "Access to all public content",
              "Community forum access",
              "Submit your films to the showcase",
              "Post and browse job opportunities",
              "Member directory listing",
              "Monthly newsletter",
              "Event notifications",
              "Workshop access"
            ]}
          ]
        }
      ]
    },
    {
      page: "workshops",
      name: "Workshops Page",
      sections: [
        {
          section: "empty_state",
          name: "Empty State (No Workshops)",
          fields: [
            { key: "headline", label: "Headline", type: "text", default: "Coming Soon" },
            { key: "message", label: "Message", type: "textarea", default: "The Society is currently curating the upcoming series of workshops. Members receive 48-hour priority access to registration before the general public." },
            { key: "button_text", label: "Button Text", type: "text", default: "Notify Me When Registration Opens" }
          ]
        }
      ]
    },
    {
      page: "jobs",
      name: "Jobs Page",
      sections: [
        {
          section: "empty_state",
          name: "Empty State (No Jobs)",
          fields: [
            { key: "headline", label: "Headline", type: "text", default: "The Industry is Hiring" },
            { key: "message", label: "Message", type: "textarea", default: "The Job Board connects organizations with the next generation of filmmakers. Listings are updated on a rolling basis as productions greenlight new roles." }
          ]
        }
      ]
    }
  ];

  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContents = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-content`, { withCredentials: true });
      setContents(response.data);
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPage = (pageConfig) => {
    setSelectedPage(pageConfig);
    // Initialize edit content with existing values or defaults
    const pageContents = {};
    pageConfig.sections.forEach(section => {
      const existing = contents.find(c => c.page === pageConfig.page && c.section === section.section);
      pageContents[section.section] = existing?.content || {};
      // Fill in defaults for missing fields
      section.fields.forEach(field => {
        if (!pageContents[section.section][field.key]) {
          pageContents[section.section][field.key] = field.default;
        }
      });
    });
    setEditContent(pageContents);
  };

  const handleFieldChange = (sectionKey, fieldKey, value) => {
    setEditContent(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [fieldKey]: value
      }
    }));
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/site-content/${selectedPage.page}/${section.section}`,
        { content: editContent[section.section] },
        { withCredentials: true }
      );
      toast.success(`${section.name} saved successfully!`);
      fetchContents();
    } catch (error) {
      toast.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async (section) => {
    if (!window.confirm(`Reset "${section.name}" to default content?`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/site-content/${selectedPage.page}/${section.section}`,
        { withCredentials: true }
      );
      toast.success("Reset to default");
      // Reset local state
      const defaults = {};
      section.fields.forEach(field => {
        defaults[field.key] = field.default;
      });
      setEditContent(prev => ({
        ...prev,
        [section.section]: defaults
      }));
      fetchContents();
    } catch (error) {
      // If not found, it's already default
      toast.info("Already using default content");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-syne text-xl font-bold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-light" />
          Site Content Editor
        </h2>
        <p className="text-slate-400 text-sm mt-1">
          Edit text and copy across the website without touching code
        </p>
      </div>

      {!selectedPage ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {editablePages.map((pageConfig) => (
            <button
              key={pageConfig.page}
              onClick={() => handleSelectPage(pageConfig)}
              data-testid={`content-page-${pageConfig.page}`}
              className="p-6 bg-void-surface border border-white/5 hover:border-purple/30 transition-all text-left group"
            >
              <h3 className="font-syne font-bold text-white mb-2 group-hover:text-purple-light transition-colors">
                {pageConfig.name}
              </h3>
              <p className="text-sm text-slate-500">
                {pageConfig.sections.length} editable section{pageConfig.sections.length !== 1 ? 's' : ''}
              </p>
            </button>
          ))}
        </div>
      ) : (
        <div>
          <button
            onClick={() => setSelectedPage(null)}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Pages
          </button>

          <h3 className="font-syne text-lg font-bold text-white mb-6">{selectedPage.name}</h3>

          <div className="space-y-6">
            {selectedPage.sections.map((section) => (
              <div key={section.section} className="p-6 bg-void-surface border border-white/5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-white">{section.name}</h4>
                  <button
                    onClick={() => handleResetToDefault(section)}
                    className="text-xs text-slate-500 hover:text-red-400 transition-colors"
                  >
                    Reset to Default
                  </button>
                </div>

                <div className="space-y-4">
                  {section.fields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-xs text-slate-500 mb-1">{field.label}</label>
                      {field.type === "textarea" ? (
                        <Textarea
                          value={editContent[section.section]?.[field.key] || ""}
                          onChange={(e) => handleFieldChange(section.section, field.key, e.target.value)}
                          rows={3}
                          className="bg-void border-white/10 text-white placeholder-slate-500"
                        />
                      ) : field.type === "benefits_list" ? (
                        <div className="space-y-2">
                          <Textarea
                            value={Array.isArray(editContent[section.section]?.[field.key]) 
                              ? editContent[section.section][field.key].join("\n") 
                              : (editContent[section.section]?.[field.key] || "")}
                            onChange={(e) => {
                              const lines = e.target.value.split("\n").filter(line => line.trim());
                              handleFieldChange(section.section, field.key, lines);
                            }}
                            rows={8}
                            placeholder="Enter one benefit per line"
                            className="bg-void border-white/10 text-white placeholder-slate-500 font-mono text-sm"
                          />
                          <p className="text-xs text-slate-500">Enter one benefit per line. Each line becomes a checkmark item.</p>
                        </div>
                      ) : (
                        <Input
                          value={editContent[section.section]?.[field.key] || ""}
                          onChange={(e) => handleFieldChange(section.section, field.key, e.target.value)}
                          className="bg-void border-white/10 text-white placeholder-slate-500"
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-white/5">
                  <Button
                    onClick={() => handleSave(section)}
                    disabled={saving}
                    className="bg-purple hover:bg-purple-hover text-white"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Newsletter Admin Component
function AdminNewsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [activeTab, setActiveTab] = useState("compose");
  const [showPreview, setShowPreview] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [formData, setFormData] = useState({
    subject: "",
    content: "",
    preview_text: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subsRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/newsletter/subscribers`, { withCredentials: true }),
        axios.get(`${API}/admin/newsletter/history`, { withCredentials: true })
      ]);
      setSubscribers(subsRes.data.subscribers || []);
      setHistory(histRes.data.newsletters || []);
    } catch (error) {
      toast.error("Failed to load newsletter data");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e, sendToSelected = false) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.content.trim()) {
      toast.error("Subject and content are required");
      return;
    }

    const targetCount = sendToSelected ? selectedEmails.size : subscribers.length;
    const targetLabel = sendToSelected ? "selected" : "all";
    
    if (sendToSelected && selectedEmails.size === 0) {
      toast.error("Please select at least one subscriber");
      return;
    }

    const estimatedTime = Math.ceil(targetCount * 0.6); // ~0.6 sec per email
    if (!window.confirm(`Send newsletter to ${targetCount} ${targetLabel} subscribers?\n\nThis may take up to ${estimatedTime} seconds. Please wait for completion.`)) {
      return;
    }

    setSending(true);
    toast.info(`Sending to ${targetCount} subscribers... Please wait.`, { duration: estimatedTime * 1000 });
    
    try {
      const payload = {
        ...formData,
        ...(sendToSelected && { emails: Array.from(selectedEmails) })
      };
      const response = await axios.post(`${API}/admin/newsletter/send`, payload, {
        withCredentials: true,
        timeout: 300000 // 5 minutes timeout for large sends
      });
      toast.success(response.data.message);
      setFormData({ subject: "", content: "", preview_text: "" });
      setSelectedEmails(new Set()); // Clear selection after send
      fetchData(); // Refresh history
      setActiveTab("history");
    } catch (error) {
      // Check if it's a timeout error - the newsletter likely still sent
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.warning("Request timed out, but the newsletter may have been sent. Check your email provider to confirm.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to send newsletter");
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!formData.subject.trim() || !formData.content.trim()) {
      toast.error("Subject and content are required");
      return;
    }

    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/admin/newsletter/send-test`, formData, {
        withCredentials: true
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  // Toggle selection of a single email
  const toggleEmailSelection = (email) => {
    setSelectedEmails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  // Select/deselect all subscribers
  const toggleSelectAll = () => {
    if (selectedEmails.size === subscribers.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(subscribers.map(s => s.email)));
    }
  };

  // Generate preview HTML with logo and tagline
  const getPreviewHTML = () => {
    const logoUrl = "https://aifilmsociety.org/images/hero-image.png";
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${formData.subject || 'Newsletter Preview'}</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #0a0a0f; color: #e2e8f0; padding: 20px; margin: 0;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #13131a; border-radius: 8px; padding: 30px;">
          <div style="text-align: center; margin-bottom: 30px;">
            <img src="${logoUrl}" alt="AI Film Society" style="width: 80px; height: 80px; margin-bottom: 15px;" />
            <h1 style="color: #a855f7; margin: 0; font-size: 28px;">AI Film Society</h1>
            <p style="color: #64748b; margin: 8px 0 0 0; font-size: 14px; font-style: italic;">A Home for AI Filmmakers</p>
          </div>
          <div style="line-height: 1.8; color: #e2e8f0;">
            ${formData.content || '<p style="color: #94a3b8; text-align: center;">Your newsletter content will appear here...</p>'}
          </div>
          <hr style="border: none; border-top: 1px solid #2d2d3d; margin: 30px 0;">
          <div style="text-align: center;">
            <img src="${logoUrl}" alt="AI Film Society" style="width: 40px; height: 40px; margin-bottom: 10px; opacity: 0.6;" />
            <p style="color: #a855f7; font-size: 14px; margin: 0 0 5px 0; font-weight: 600;">AI Film Society</p>
            <p style="color: #64748b; font-size: 12px; margin: 0 0 15px 0; font-style: italic;">Empowering creators to shape the future of cinema</p>
            <p style="font-size: 11px; color: #475569; margin: 0;">
              You're receiving this because you subscribed to our newsletter.
            </p>
            <p style="margin: 10px 0 0 0;">
              <a href="#" style="color: #a855f7; font-size: 11px;">Unsubscribe</a>
              <span style="color: #475569; margin: 0 8px;">|</span>
              <a href="#" style="color: #a855f7; font-size: 11px;">Visit Website</a>
            </p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-xl font-bold text-white flex items-center gap-2">
          <Mail className="w-5 h-5 text-purple-light" />
          Newsletter Management
        </h2>
        <span className="text-sm text-slate-400">
          {subscribers.length} subscribers
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {["compose", "subscribers", "history"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg font-medium capitalize transition-colors ${
              activeTab === tab
                ? "bg-purple/20 text-purple-light border-b-2 border-purple"
                : "text-slate-400 hover:text-white"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Compose Tab */}
      {activeTab === "compose" && (
        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Subject *</label>
            <Input
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Newsletter subject line"
              className="bg-void-surface border-white/10 text-white"
              data-testid="newsletter-subject"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Preview Text</label>
            <Input
              value={formData.preview_text}
              onChange={(e) => setFormData({ ...formData, preview_text: e.target.value })}
              placeholder="Short preview text for email clients"
              className="bg-void-surface border-white/10 text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Content * (HTML supported)</label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="<h2>Hello!</h2><p>Your newsletter content here...</p>"
              rows={12}
              className="bg-void-surface border-white/10 text-white font-mono text-sm"
              data-testid="newsletter-content"
            />
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4">
            <div className="text-sm text-slate-400">
              <p>
                Total: <span className="text-purple-light font-medium">{subscribers.length}</span> subscribers
                {selectedEmails.size > 0 && (
                  <span className="ml-2">
                    | Selected: <span className="text-green-400 font-medium">{selectedEmails.size}</span>
                  </span>
                )}
              </p>
              {selectedEmails.size > 0 && (
                <button 
                  type="button"
                  onClick={() => setSelectedEmails(new Set())}
                  className="text-xs text-slate-500 hover:text-slate-300 underline mt-1"
                >
                  Clear selection
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPreview(true)}
                className="border-purple/50 text-purple-light hover:bg-purple/10"
                data-testid="preview-newsletter-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSendTest}
                disabled={sendingTest}
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                data-testid="send-test-btn"
              >
                {sendingTest ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Send Test to Me
              </Button>
              {selectedEmails.size > 0 && (
                <Button
                  type="button"
                  onClick={(e) => handleSend(e, true)}
                  disabled={sending}
                  className="bg-green-600 hover:bg-green-500"
                  data-testid="send-selected-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send to Selected ({selectedEmails.size})
                </Button>
              )}
              <Button
                type="submit"
                disabled={sending}
                className="bg-purple hover:bg-purple-light"
                data-testid="send-newsletter-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send to All
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-void-surface border border-white/10 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h3 className="font-syne font-bold text-white text-lg">Email Preview</h3>
                <p className="text-sm text-slate-400">Subject: {formData.subject || '(No subject)'}</p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-slate-900">
              <div className="rounded-lg overflow-hidden shadow-2xl">
                <iframe
                  srcDoc={getPreviewHTML()}
                  title="Newsletter Preview"
                  className="w-full h-[500px] bg-[#0a0a0f]"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPreview(false)}
                className="border-white/20 text-slate-300"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setShowPreview(false);
                  if (formData.subject && formData.content) {
                    handleSend({ preventDefault: () => {} });
                  } else {
                    toast.error("Please fill in subject and content first");
                  }
                }}
                className="bg-purple hover:bg-purple-light"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Newsletter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Subscribers Tab */}
      {activeTab === "subscribers" && (
        <div className="space-y-4">
          {/* Selection controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSelectAll}
                className="text-sm text-purple-light hover:text-white transition-colors"
              >
                {selectedEmails.size === subscribers.length ? "Deselect All" : "Select All"}
              </button>
              {selectedEmails.size > 0 && (
                <span className="text-sm text-slate-400">
                  {selectedEmails.size} of {subscribers.length} selected
                </span>
              )}
            </div>
            {selectedEmails.size > 0 && (
              <Button
                onClick={() => setActiveTab("compose")}
                size="sm"
                className="bg-green-600 hover:bg-green-500"
              >
                <Send className="w-4 h-4 mr-2" />
                Send to Selected ({selectedEmails.size})
              </Button>
            )}
          </div>
          
          <div className="bg-void-surface border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedEmails.size === subscribers.length && subscribers.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-white/20 bg-void-surface text-purple focus:ring-purple"
                    />
                  </th>
                  <th className="text-left text-sm font-medium text-slate-300 px-4 py-3">Email</th>
                  <th className="text-left text-sm font-medium text-slate-300 px-4 py-3">Name</th>
                  <th className="text-left text-sm font-medium text-slate-300 px-4 py-3">Source</th>
                  <th className="text-left text-sm font-medium text-slate-300 px-4 py-3">Subscribed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {subscribers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-slate-400 py-8">
                      No subscribers yet
                    </td>
                  </tr>
                ) : (
                  subscribers.map((sub, idx) => (
                    <tr 
                      key={idx} 
                      className={`hover:bg-white/5 cursor-pointer ${selectedEmails.has(sub.email) ? 'bg-purple/10' : ''}`}
                      onClick={() => toggleEmailSelection(sub.email)}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedEmails.has(sub.email)}
                          onChange={() => toggleEmailSelection(sub.email)}
                          className="rounded border-white/20 bg-void-surface text-purple focus:ring-purple"
                        />
                      </td>
                      <td className="px-4 py-3 text-white">{sub.email}</td>
                      <td className="px-4 py-3 text-slate-300">{sub.name || "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          sub.source === "registration" 
                            ? "bg-green-500/20 text-green-400"
                            : "bg-purple/20 text-purple-light"
                        }`}>
                          {sub.source}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm">
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <div className="space-y-4">
          {history.length === 0 ? (
            <div className="text-center text-slate-400 py-12">
              No newsletters sent yet
            </div>
          ) : (
            history.map((nl, idx) => (
              <div key={idx} className="bg-void-surface border border-white/10 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-white">{nl.subject}</h3>
                    {nl.is_selective && (
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                        Selective
                      </span>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    nl.status === "sent" 
                      ? "bg-green-500/20 text-green-400"
                      : "bg-red-500/20 text-red-400"
                  }`}>
                    {nl.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                  <span>
                    Sent: {nl.recipient_count}
                    {nl.total_attempted && nl.total_attempted !== nl.recipient_count && (
                      <span className="text-slate-500"> / {nl.total_attempted} attempted</span>
                    )}
                  </span>
                  {nl.failed_count > 0 && (
                    <span className="text-red-400">Failed: {nl.failed_count}</span>
                  )}
                  <span>By: {nl.sent_by}</span>
                  <span>{new Date(nl.sent_at).toLocaleString()}</span>
                </div>
                {nl.failed_count > 0 && (
                  <div className="mt-2 text-xs text-amber-400 bg-amber-500/10 px-3 py-2 rounded">
                    ⚠️ {nl.failed_count} email(s) failed to send. This could be due to invalid email addresses, 
                    rate limits, or email service issues. Check server logs for details.
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Backup & Restore Admin Component
function AdminBackup() {
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useState(null);

  const handleBackup = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/backup`, { 
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aifilmsociety_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Backup downloaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Backup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event, mode) => {
    const file = event.target.files[0];
    if (!file) return;

    const isRestore = mode === 'restore';
    if (isRestore) setRestoring(true);
    else setImporting(true);

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      const endpoint = isRestore ? 'restore' : 'import';
      const response = await axios.post(`${API}/admin/${endpoint}`, data, { withCredentials: true });
      
      const result = isRestore ? response.data.restored : response.data.imported;
      const counts = Object.entries(result)
        .filter(([_, count]) => count > 0)
        .map(([name, count]) => `${name}: ${count}`)
        .join(', ');
      
      toast.success(`${isRestore ? 'Restored' : 'Imported'}: ${counts || 'No new items'}`);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON file");
      } else {
        toast.error(error.response?.data?.detail || `${mode} failed`);
      }
    } finally {
      setRestoring(false);
      setImporting(false);
      event.target.value = '';
    }
  };

  return (
    <div>
      <h2 className="font-syne text-xl font-bold text-white mb-6">Backup & Restore</h2>
      
      <div className="space-y-8">
        {/* Backup Section */}
        <div className="p-6 bg-void-surface border border-white/5">
          <h3 className="font-syne text-lg text-white mb-2 flex items-center gap-2">
            <Download className="w-5 h-5 text-green-400" />
            Export Backup
          </h3>
          <p className="text-slate-400 text-sm mb-4">
            Download all your content (news, films, directors, events, etc.) as a JSON file. 
            Use this to backup your data or transfer it to another environment.
          </p>
          <Button
            onClick={handleBackup}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download Backup
          </Button>
        </div>

        {/* Restore Section */}
        <div className="p-6 bg-void-surface border border-white/5">
          <h3 className="font-syne text-lg text-white mb-2 flex items-center gap-2">
            <Upload className="w-5 h-5 text-yellow-400" />
            Restore from Backup
          </h3>
          <p className="text-slate-400 text-sm mb-2">
            <strong className="text-yellow-400">Warning:</strong> This will REPLACE all existing content with the backup data.
          </p>
          <p className="text-slate-400 text-sm mb-4">
            Use this to restore from a backup file. All current content will be deleted first.
          </p>
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white cursor-pointer transition-colors">
            {restoring ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {restoring ? "Restoring..." : "Restore Backup"}
            <input
              type="file"
              accept=".json"
              onChange={(e) => handleFileUpload(e, 'restore')}
              className="hidden"
              disabled={restoring}
            />
          </label>
        </div>

        {/* Import Section */}
        <div className="p-6 bg-void-surface border border-white/5">
          <h3 className="font-syne text-lg text-white mb-2 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-400" />
            Import Content (Merge)
          </h3>
          <p className="text-slate-400 text-sm mb-4">
            Import content from a backup file without deleting existing content. 
            Only new items (with unique IDs) will be added. Existing items are skipped.
          </p>
          <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white cursor-pointer transition-colors">
            {importing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {importing ? "Importing..." : "Import Content"}
            <input
              type="file"
              accept=".json"
              onChange={(e) => handleFileUpload(e, 'import')}
              className="hidden"
              disabled={importing}
            />
          </label>
        </div>

        {/* Instructions */}
        <div className="p-6 bg-purple/10 border border-purple/20">
          <h3 className="font-syne text-lg text-white mb-3">How to Transfer Content</h3>
          <ol className="text-slate-300 text-sm space-y-2 list-decimal list-inside">
            <li>On the <strong>source site</strong> (e.g., Preview), go to Admin → Backup</li>
            <li>Click "Download Backup" to save your content as JSON</li>
            <li>On the <strong>target site</strong> (e.g., Production), go to Admin → Backup</li>
            <li>Use "Restore Backup" to replace all content, or "Import Content" to add new items</li>
          </ol>
          <p className="text-slate-400 text-sm mt-4">
            <strong>Tip:</strong> Take regular backups of your production site to prevent data loss!
          </p>
        </div>
      </div>
    </div>
  );
}
